import { Box, Typography } from "@mui/joy";
import { useState } from "react";

import { FadeIn } from "~/common/components";

interface Stat {
  value: string;
  label: string;
}

const stats: Stat[] = [
  {
    value: "80%",
    label: "менше часу на перевірки реєстру",
  },
  {
    value: "10 000+",
    label: "нагадувань вже надіслано",
  },
  {
    value: "3 000+",
    label: "заявок перевіряємо щодня",
  },
  {
    value: "99.9%",
    label: "доступність сервісу",
  },
];

const GLOW_COLOR = "rgba(167, 163, 235, 0.6)";
const LINE_COLOR = "rgba(255, 255, 255, 0.12)";
const COLUMN_COUNT = stats.length;

const SEGMENT_WIDTH = `${100 / COLUMN_COUNT}%`;

/** Stats section showcasing key platform metrics */
export const Stats = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  const glowLeft = `${(activeIndex / COLUMN_COUNT) * 100}%`;

  const glowBarSx = {
    position: "absolute",
    width: SEGMENT_WIDTH,
    height: "3px",
    left: glowLeft,
    background: GLOW_COLOR,
    boxShadow: `0 0 12px 2px ${GLOW_COLOR}`,
    transition: "left 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    pointerEvents: "none",
  } as const;

  return (
    <Box
      component="section"
      sx={{
        width: "100%",
        background:
          "radial-gradient(1655px 1133px at 50% calc(100% + 69px),#fcfdfe 1.11%,#6763e4 28.73%,#453bb3 45.76%,#29227d 63.37%,#1e2064 78.67%,#141e4b 100%)",
        py: { xs: 5, sm: 7, md: 9 },
        px: { xs: 2, sm: 3, md: 4, lg: 6 },
        position: "relative",
      }}
    >
      {/* Left vertical line — full section height */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: {
            xs: 16,
            sm: 24,
            md: 32,
            lg: `max(48px, calc((100% - 1200px) / 2))`,
          },
          width: "1px",
          background: LINE_COLOR,
          pointerEvents: "none",
        }}
      />
      {/* Right vertical line — full section height */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          bottom: 0,
          right: {
            xs: 16,
            sm: 24,
            md: 32,
            lg: `max(48px, calc((100% - 1200px) / 2))`,
          },
          width: "1px",
          background: LINE_COLOR,
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: { xs: 5, md: 7 },
        }}
      >
        <FadeIn offsetY={20} duration={300} delay={100}>
          <Typography
            level="h2"
            sx={{
              fontWeight: 500,
              fontSize: {
                xs: "1.75rem",
                sm: "2.25rem",
                md: "2.75rem",
                lg: "3.25rem",
              },
              lineHeight: 1.15,
              letterSpacing: "-0.02em",
              color: "common.white",
              textAlign: "center",
              textWrap: "balance",
            }}
          >
            Надійний фундамент
            <br />
            для вашої IP-практики
          </Typography>
        </FadeIn>

        {/* Stats grid with border lines */}
        <Box sx={{ width: "100%", position: "relative" }}>
          {/* Top horizontal line */}
          <Box
            sx={{
              width: "100%",
              height: "1px",
              background: LINE_COLOR,
              position: "relative",
              zIndex: 2,
            }}
          >
            <Box sx={{ ...glowBarSx, top: "-1px" }} />
          </Box>

          {/* Stats row */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, 1fr)",
                md: `repeat(${COLUMN_COUNT}, 1fr)`,
              },
              position: "relative",
              zIndex: 2,
            }}
          >
            {stats.map((stat, index) => {
              const isActive = activeIndex === index;

              return (
                <FadeIn
                  key={stat.value}
                  offsetY={20}
                  duration={300}
                  delay={200 + index * 100}
                >
                  <Box
                    onMouseEnter={() => setActiveIndex(index)}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      gap: 1.5,
                      py: { xs: 4, md: 5 },
                      px: 2,
                      cursor: "pointer",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 400,
                        fontSize: {
                          xs: "2rem",
                          sm: "2.5rem",
                          md: "3rem",
                          lg: "3.5rem",
                        },
                        lineHeight: 1.1,
                        letterSpacing: "-0.02em",
                        color: "common.white",
                        opacity: isActive ? 1 : 0.7,
                        transition: "opacity 0.4s ease",
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: {
                          xs: "0.813rem",
                          sm: "0.875rem",
                          md: "0.938rem",
                        },
                        fontWeight: 400,
                        lineHeight: 1.5,
                        color: "common.white",
                        opacity: isActive ? 0.8 : 0.7,
                        transition: "opacity 0.4s ease",
                        maxWidth: "20ch",
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                </FadeIn>
              );
            })}
          </Box>

          {/* Bottom horizontal line */}
          <Box
            sx={{
              width: "100%",
              height: "1px",
              background: LINE_COLOR,
              position: "relative",
              zIndex: 2,
            }}
          >
            <Box sx={{ ...glowBarSx, bottom: "-1px" }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
